import { queueRevocation } from "./privacy/revocations";
import type { Database } from "./database.types";
import { apiRequest } from "./apiClient";
import {
  configureOwner,
  getStorageOwner,
  ownerStorage,
} from "./privacy/ownerStorage";
type Profile = Database["public"]["Tables"]["profiles"]["Update"];
type Progress = Database["public"]["Tables"]["progress"]["Update"];
export type SyncStatus = "synced" | "pending" | "offline" | "error";
interface PendingChange {
  id: string;
  ownerId: string;
  tenantId: string;
  type:
    | "profile"
    | "progress"
    | "mood_checkin"
    | "technique_session"
    | "stress_entry"
    | "feedback"
    | "snapshot";
  data: Record<string, unknown>;
  timestamp: string;
  baseRevision?: number;
}
export class SyncService {
  private userId: string | null = null;
  private authenticated = false;
  private pendingChanges: PendingChange[] = [];
  private status: SyncStatus = "offline";
  private listeners = new Set<(status: SyncStatus) => void>();
  private epoch = 0;
  private syncing = false;
  private inFlightId: string | null = null;
  constructor() {
    if (typeof window !== "undefined")
      window.addEventListener("online", () => void this.syncPendingChanges());
  }
  setIdentity(id: string, authenticated: boolean) {
    this.epoch++;
    this.userId = id;
    this.authenticated = authenticated;
    this.pendingChanges = [];
    this.setStatus("offline");
  }
  configureScope(tenantId: string, persistent: boolean) {
    if (!this.userId) return;
    this.epoch++;
    configureOwner({ tenantId, userId: this.userId, persistent });
    try {
      const parsed: unknown = JSON.parse(
        ownerStorage.getItem("sync-queue") || "[]",
      );
      this.pendingChanges = Array.isArray(parsed)
        ? parsed.filter(
            (change): change is PendingChange =>
              change?.ownerId === this.userId &&
              change?.tenantId === tenantId &&
              typeof change?.id === "string",
          )
        : [];
    } catch {
      this.setStatus("error");
      return;
    }
    this.setStatus(this.pendingChanges.length ? "pending" : "offline");
  }
  getUserId() {
    return this.userId;
  }
  setUserId(id: string) {
    this.setIdentity(id, false);
  }
  generateUserId() {
    const id = crypto.randomUUID();
    this.setIdentity(id, false);
    return id;
  }
  getStatus() {
    return this.status;
  }
  onStatusChange(callback: (status: SyncStatus) => void) {
    this.listeners.add(callback);
    callback(this.status);
    return () => this.listeners.delete(callback);
  }
  private setStatus(status: SyncStatus) {
    this.status = status;
    this.listeners.forEach((listener) => listener(status));
  }
  isCloudEnabled() {
    return this.authenticated && ownerStorage.getItem("cloud-sync") === "true";
  }
  async setCloudEnabled(enabled: boolean) {
    if (!this.authenticated)
      throw new Error("Sign in before choosing cloud backup.");
    if (!enabled) {
      ownerStorage.setItem("cloud-sync", "false");
      this.pendingChanges = [];
      this.persistQueue();
      this.setStatus("offline");
      await queueRevocation("cloud_sync");
      return;
    }
    const owner = this.userId;
    const epoch = this.epoch;
    await apiRequest("/privacy/consent", {
      method: "POST",
      body: JSON.stringify({ scope: "cloud_sync", granted: enabled }),
    });
    if (owner !== this.userId || epoch !== this.epoch) return;
    ownerStorage.setItem("cloud-sync", String(enabled));
    if (!enabled) {
      this.pendingChanges = [];
      this.persistQueue();
      this.setStatus("offline");
    } else await this.syncPendingChanges();
  }
  private persistQueue() {
    if (
      !ownerStorage.setItem("sync-queue", JSON.stringify(this.pendingChanges))
    )
      this.setStatus("error");
  }
  private async queue(
    type: PendingChange["type"],
    data: Record<string, unknown>,
  ) {
    if (!this.userId || !this.isCloudEnabled()) return;
    const scope = getStorageOwner();
    const change: PendingChange = {
      id: crypto.randomUUID(),
      ownerId: this.userId,
      tenantId: scope.tenantId,
      type,
      data,
      baseRevision: Number(ownerStorage.getItem("cloud-revision") || 0),
      timestamp: new Date().toISOString(),
    };
    // A snapshot replaces only an older snapshot for this same owner. Append-only receipts are never discarded.
    if (type === "snapshot")
      this.pendingChanges = this.pendingChanges.filter(
        (item) => item.type !== "snapshot" || item.id === this.inFlightId,
      );
    this.pendingChanges.push(change);
    this.setStatus("pending");
    this.persistQueue();
    await this.syncPendingChanges();
  }
  saveProfile(data: Partial<Profile>) {
    return this.queue("profile", data as Record<string, unknown>);
  }
  saveProgress(data: Partial<Progress>) {
    return this.queue("progress", data as Record<string, unknown>);
  }
  saveMoodCheckIn(data: { date: string; mood: "stressed" | "okay" | "good" }) {
    return this.queue("mood_checkin", data);
  }
  saveTechniqueSession(data: {
    technique: string;
    duration: number;
    pre_stress?: number | null;
    post_stress?: number | null;
  }) {
    return this.queue("technique_session", data);
  }
  saveStressEntry(data: {
    symptoms: string[];
    fuel_before: number;
    fuel_after: number;
    reflection?: string | null;
  }) {
    return this.queue("stress_entry", data);
  }
  saveFeedback(data: {
    user_id: string;
    category: string;
    message: string;
    rating: number | null;
    page_context: string | null;
  }) {
    return this.queue("feedback", data);
  }
  saveSnapshot(data: Record<string, unknown>) {
    return this.queue("snapshot", data);
  }
  async syncPendingChanges(): Promise<void> {
    if (
      !this.isCloudEnabled() ||
      this.syncing ||
      !navigator.onLine ||
      !this.pendingChanges.length
    )
      return;
    const epoch = this.epoch;
    const ownerId = this.userId;
    this.syncing = true;
    try {
      while (this.pendingChanges.length) {
        const change = this.pendingChanges[0];
        if (
          epoch !== this.epoch ||
          ownerId !== this.userId ||
          !this.isCloudEnabled()
        )
          break;
        if (
          change.ownerId !== ownerId ||
          change.tenantId !== getStorageOwner().tenantId
        )
          throw new Error("Queue ownership mismatch");
        this.inFlightId = change.id;
        const result = await apiRequest<{ revision?: number }>(
          "/personal/sync",
          { method: "POST", body: JSON.stringify(change) },
        );
        if (epoch !== this.epoch || ownerId !== this.userId) break;
        if (result.revision !== undefined) {
          ownerStorage.setItem("cloud-revision", String(result.revision));
          this.pendingChanges = this.pendingChanges.map((item) =>
            item.type === "snapshot" &&
            item.id !== change.id &&
            item.baseRevision === change.baseRevision
              ? { ...item, baseRevision: result.revision }
              : item,
          );
        }
        this.pendingChanges = this.pendingChanges.filter(
          (item) => item.id !== change.id,
        );
        this.persistQueue();
      }
      if (epoch === this.epoch) {
        this.setStatus(this.pendingChanges.length ? "pending" : "synced");
        ownerStorage.setItem("last-sync", new Date().toISOString());
      }
    } catch {
      if (epoch === this.epoch) this.setStatus("error");
    } finally {
      this.inFlightId = null;
      this.syncing = false;
      if (epoch !== this.epoch) void this.syncPendingChanges();
    }
  }
  async loadFromCloud(): Promise<{
    profile: Profile | null;
    progress: Progress | null;
    snapshot?: Record<string, unknown>;
    revision?: number;
  } | null> {
    if (!this.isCloudEnabled()) return null;
    const epoch = this.epoch;
    try {
      const data = await apiRequest<{
        profile: Profile | null;
        progress: Progress | null;
        snapshot?: Record<string, unknown>;
        revision?: number;
      }>("/personal/state");
      if (epoch !== this.epoch) return null;

      return data;
    } catch {
      if (epoch === this.epoch) this.setStatus("error");
      return null;
    }
  }
  acceptCloudRevision(revision: number) {
    this.pendingChanges = [];
    this.persistQueue();
    ownerStorage.setItem("cloud-revision", String(revision));
  }
  async deleteCloudData() {
    const epoch = this.epoch;
    await apiRequest("/privacy/data", { method: "DELETE" });
    if (epoch !== this.epoch) return;
    this.pendingChanges = [];
    ownerStorage.setItem("cloud-sync", "false");
    this.persistQueue();
    this.setStatus("offline");
  }
  async checkAtRiskIndicators(_data: {
    stressLevel: number;
    recentStressLevels: number[];
    crisisButtonPressed?: boolean;
  }): Promise<void> {
    // A self-rating is not a validated risk classifier. Support requests use an explicit consented workflow.
  }
}
export const syncService = new SyncService();
